import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';
import Pool from '@/services/auth/userPool';
import { type SignUpFormState, SignUpStage } from './types';

export const useSignUp = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | boolean>(false);
  const [stage, setStage] = useState<SignUpStage>(SignUpStage.PERSONAL_DETAILS);
  const [form, setForm] = useState<SignUpFormState>({
    name: '',
    phoneNumber: '9546738542',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Handle session checkout override from URL
  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get('session_id');
    if (sessionId) {
      setLoading(true);
      setStage(SignUpStage.SUCCESS);

      const userId = localStorage.getItem('id');
      fetch(`${BASE_URL}${ENDPOINTS.checkoutSession}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          session_id: sessionId,
        }),
      })
        .then(() => {
          setLoading(false);
          localStorage.removeItem('id');
        })
        .catch(() => {
          setError('Error checking out');
          setLoading(false);
          localStorage.removeItem('id');
        });
    }
  }, [location.search]);

  const signUp = () => {
    setLoading(true);

    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: form.email }),
      new CognitoUserAttribute({ Name: 'name', Value: form.name }),
      new CognitoUserAttribute({ Name: 'phone_number', Value: `+1${form.phoneNumber}` }),
    ];

    Pool.signUp(form.email, form.password, attributeList, [], (err, data) => {
      if (err) {
        setLoading(false);
        setError(`Trouble signing up: ${err.message || err}`);
      } else if (data) {
        fetch(`${BASE_URL}${ENDPOINTS.userRegister}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: data.userSub,
            full_name: form.name,
            email: data.user.getUsername(),
            phone_number: form.phoneNumber,
          }),
        })
          .then((res) => {
            if (!res.ok) throw new Error('Failed to register');
            return res.json();
          })
          .then((res) => {
            localStorage.setItem('id', res.cognito_user_id);
            setLoading(false);
            setStage(SignUpStage.SUBSCRIPTION);
          })
          .catch(() => {
            setLoading(false);
            setError('Trouble signing up. Please try again');
          });
      }
    });
  };

  return {
    form,
    setForm,
    stage,
    setStage,
    loading,
    error,
    setError,
    signUp,
    navigate,
  };
};
